import React from 'react';

import Utilities from '../common/Utilities.jsx';
import useOwnerRealm from '../common/useOwnerRealm.jsx';

const Position = {
    Above: 'above',
    Below: 'below',
    Visible: 'visible'
}

function Landmark(props) {
    const [id, setId] = React.useState(undefined);
    React.useEffect(() => setId(Utilities.generateUniqueId()), []);
    if (!id) {
        return null;
    }
    return <_Landmark {...props} />
}

function _Landmark(props) {
    // The landmark span doubles as the realm anchor, so scroll/resize reads target
    // the iframe's window when rendered inside a builder preview.
    const { getDocument, getWindow, rootRef: landmarkRef } = useOwnerRealm();
    // Resolved lazily — the realm is only known once the span is attached.
    const getContainerElement = () => props.containerRef ?? getDocument().body;

    // Scroll listener
    const [scrollInfo, setScrollInfo] = React.useState({ x: 0, y: 0 });
    const scrollEventListenerAdded = React.useRef(false);

    const scrollDependencyList = [];
    if (!props.useWindow) {
        scrollDependencyList.push(props.containerRef);
    }
    const handleScroll = () => {
        const containerElement = getContainerElement();
        const win = getWindow();
        let x = containerElement && containerElement.scrollLeft;
        if (x == undefined || win.scrollX > x) {
            x = win.scrollX;
        }
        let y = containerElement && containerElement.scrollTop;
        if (y == undefined || win.scrollY > y) {
             y = win.scrollY
        }
        setScrollInfo({ x, y });
    };
    React.useEffect(() => {
        const containerElement = props.useWindow ? getWindow() : getContainerElement();
        if (containerElement && scrollEventListenerAdded.current === false) {
            containerElement.addEventListener('scroll', handleScroll, { passive: true });
        }
        scrollEventListenerAdded.current = true;

        return () => {
            if (scrollEventListenerAdded && containerElement) {
                containerElement.removeEventListener('scroll', handleScroll);
            }
            scrollEventListenerAdded.current = false;
        };
    }, scrollDependencyList);

    const [active, setActive] = React.useState(undefined);
    React.useEffect(() => {
        if (!active && props.active) {
            setActive(true);
            setTimeout(handleScroll, 0);
        }
    }, [props.active]);

    // Resize listener
    const getDimensions = () => {
        if (props.onDimensions) {
            return props.onDimensions();
        }
        const containerElement = getContainerElement();
        if (!containerElement) {
            const win = getWindow();
            return { height: win.innerHeight, width: win.innerWidth };
        }
        return { height: containerElement.offsetHeight, width: containerElement.offsetWidth };
    }
    const [containerRect, setContainerRect] = React.useState({ bottom: 0, left: 0, right: 0, top: 0 });
    // Measured on mount — the realm isn't known during the first render.
    const [dimensions, setDimensions] = React.useState({ height: 0, width: 0 });
    const resizeTimeout = React.useRef(undefined);
    const resizeEventListenerAdded = React.useRef(false);
    const updateDimensions = () => {
        setDimensions(getDimensions())
        const containerElement = getContainerElement();
        if (containerElement) {
            const rect = containerElement.getBoundingClientRect();
            if (props.useWindow) {
                rect.y += getWindow().scrollY;
            }
            setContainerRect(rect);
        }
    }

    React.useEffect(() => {
        updateDimensions();

        function handleResize() {
            if (resizeTimeout.current) {
                clearTimeout(resizeTimeout.current);
            }
            resizeTimeout.current = setTimeout(updateDimensions, props.resizeTimeout || 15);
        }
        // Captured so cleanup detaches from the same window the listener was added to.
        const win = getWindow();
        if (resizeEventListenerAdded.current === false) {
            win.addEventListener('resize', handleResize, { passive: true });
        }
        resizeEventListenerAdded.current = true;

        return () => {
            win.removeEventListener('resize', handleResize);
            resizeEventListenerAdded.current = false;
        };
    }, [props.containerRef]);

    // Position handling
    const previousPositionRef = React.useRef(undefined);
    const [delayed, setDelayed] = React.useState(false);
    const [discovered, setDiscovered] = React.useState(false);
    const [position, setPosition] = React.useState(undefined);

    React.useLayoutEffect(() => {
        if (delayed || props.active == false) {
            return;
        }
        if (landmarkRef.current) {
            const rect = landmarkRef.current.getBoundingClientRect();
            const offsetBottom = (props.offsetBottom || 0) - containerRect.top;
            const offsetLeft = (props.offsetLeft || 0) + containerRect.left;
            const offsetRight = (props.offsetRight || 0) - containerRect.left;
            const offsetTop = (props.offsetTop || 0) + containerRect.top;
            if (rect.top >= offsetTop &&
                rect.bottom < dimensions.height - offsetBottom &&
                rect.left >= offsetLeft &&
                rect.right < dimensions.width - offsetRight) {
                setPosition(Position.Visible);
            } else if (rect.top > dimensions.height - offsetBottom) {
                setPosition(Position.Below);
            } else {
                setPosition(Position.Above);
            }
        }
    }, [landmarkRef, scrollInfo, dimensions, delayed]);

    React.useLayoutEffect(() => {
        if (delayed || props.active == false) {
            return;
        }
        if (previousPositionRef.current === undefined && position !== undefined) {
            previousPositionRef.current = position;

            if (position == Position.Visible) {
                // Visible on load
                if (props.onEnter) {
                    if (!discovered) {
                        setDelayed(true);
                        setPosition(undefined);
                        previousPositionRef.current = undefined;
                        setTimeout(() => setDelayed(false), 0);
                    } else {
                        props.onEnter();
                    }
                }
                setDiscovered(true);
            }
        }
        const _enter = () => {
            setDiscovered(true);

            if (props.onEnter) {
                props.onEnter(previousPositionRef.current);
            }
        }
        const _leave = () => {
            if (props.onLeave) {
                props.onLeave(position);
            }
        }
        if (position === Position.Visible && (previousPositionRef.current === Position.Below || previousPositionRef.current === Position.Above)) {
            // Entered from above or below
            _enter();
            previousPositionRef.current = position;
        }
        if ((position == Position.Above || position == Position.Below) && previousPositionRef.current == Position.Visible) {
            // No longer visible => Update previous position
            previousPositionRef.current = position;
            _leave();
        }
        if ((position == Position.Above && previousPositionRef.current == Position.Below) || (position == Position.Below && previousPositionRef.current == Position.Above)) {
            // Rapid scroll => Fire the events
            _enter();
            _leave();
        }
    }, [position]);

    return <span ref={landmarkRef} style={{ fontSize: 0 }} />
}

function TestViewNav() {
    const [sticky, setSticky] = React.useState(false);
    const handleEnter = (position) => {
        setSticky(false);
    }
    const handleLeave = (position) => {
        if (position == Position.Below) {
            return;
        }
        setSticky(true);
    }
    return (
        <div style={{ height: '200%', position: 'relative' }}>
            <div style={{ height: '25%' }} />
            <div>
                <Landmark onEnter={handleEnter} onLeave={handleLeave} />
                <div style={{ background: 'darkblue', color: 'white', position: sticky ? 'fixed' : 'relative', top: 0, width: '100%' }}>
                    <div style={{ height: '40px', lineHeight: '40px' }}>Landmark 1</div>
                </div>
                <div style={{ background: 'darkblue', color: 'white' }}>
                    <div style={{ display: sticky ? 'block' : 'none', height: '40px', lineHeight: '40px' }}>Landmark 1</div>
                </div>
            </div>
        </div>
    )
}

function TestViewGrid() {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', padding: '20px 0 20px 20px' }}>
            {Array.from(Array(40).keys()).map(d => <GridView key={d} index={d} />)}
        </div>
    )
}

function GridView(props) {
    const [animate, setAnimate] = React.useState(false);
    const handleEnter = () => setAnimate(true);
    return (
        <div style={{
            background: 'black',
            color: 'white',
            fontSize: '40px',
            height: '100px',
            lineHeight: '100px',
            opacity: animate ? 1 : 0,
            margin: '20px',
            textAlign: 'center',
            transition: 'opacity 1s ease-in-out',
            width: 'calc(33% - 40px)'
        }}>
            <Landmark onEnter={handleEnter} />
            {props.index}
        </div>
    )
}

export { Landmark as Component, Position, TestViewGrid, TestViewNav };